/**
 * IBAN-/BIC-Validierung ohne externe Abhängigkeit (Issue #899).
 *
 * Bis 09/2026 galt eine IBAN als gültig, sobald ihre Bankleitzahl in einer Bank-Tabelle
 * stand — ein Zahlendreher in der Kontonummer fiel erst bei der Bank auf. Die Mod-97-
 * Prüfziffer (ISO 13616) ist deterministisch, gilt für alle Länder und fängt genau das.
 *
 * Node 14 / TS 4.7: kein BigInt, Modulo blockweise.
 */

export const normalizeIban = (iban: string | null | undefined): string => (iban ?? "").replace(/\s/g, "").toUpperCase();

export const normalizeBic = (bic: string | null | undefined): string => (bic ?? "").replace(/\s/g, "").toUpperCase();

const IBAN_SHAPE = /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/;

/** IBAN-Länge je Land (SEPA-Raum + gängige Nachbarn). Unbekanntes Land: nur Shape + Mod-97. */
const IBAN_LENGTHS: {[countryCode: string]: number} = {
    AD: 24, AT: 20, BE: 16, BG: 22, CH: 21, CY: 28, CZ: 24, DE: 22, DK: 18, EE: 20, ES: 24, FI: 18, FR: 27,
    GB: 22, GI: 23, GR: 27, HR: 21, HU: 28, IE: 22, IS: 26, IT: 27, LI: 21, LT: 20, LU: 20, LV: 21, MC: 27,
    MT: 31, NL: 18, NO: 15, PL: 28, PT: 25, RO: 24, SE: 24, SI: 19, SK: 24, SM: 27, VA: 22,
};

/** Länder, deren BBAN rein numerisch ist (Buchstaben im BBAN wären dort ein Tippfehler). */
// Laut IBAN-Registry rein numerisch; NICHT dabei: LU (3!n13!c), IT/SM, MT, CY, NL, GB, IE, CH/LI, FR, BG, LV, RO, GR, IS.
const NUMERIC_BBAN_COUNTRIES = ["AT", "BE", "CZ", "DE", "DK", "EE", "ES", "FI", "HR", "HU", "LT", "NO", "PL", "PT", "SE", "SI", "SK"];

/** ISO 9362: 4 Bank, 2 Land, 2 Ort, optional 3 Filiale. */
const BIC_SHAPE = /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/;

/** Mod-97-Prüfziffer nach ISO 13616 (Struktur + Prüfziffer, keine Bank-Tabelle). */
export const isValidIban = (ibanRaw: string | null | undefined): boolean => {
    const iban = normalizeIban(ibanRaw);

    if (!IBAN_SHAPE.test(iban)) {
        return false;
    }

    const countryCode = iban.slice(0, 2);
    const expectedLength = IBAN_LENGTHS[countryCode];

    if (expectedLength !== undefined && iban.length !== expectedLength) {
        return false;
    }
    if (NUMERIC_BBAN_COUNTRIES.indexOf(countryCode) !== -1 && !/^\d+$/.test(iban.slice(4))) {
        return false;
    }

    const rearranged = iban.slice(4) + iban.slice(0, 4);
    const numeric = rearranged.replace(/[A-Z]/g, (ch) => String(ch.charCodeAt(0) - 55));
    let remainder = 0;

    for (let i = 0; i < numeric.length; i += 7) {
        remainder = parseInt(String(remainder) + numeric.slice(i, i + 7), 10) % 97;
    }

    return remainder === 1;
};

/** Nur das Format (8 oder 11 Zeichen) — ob die BIC existiert, prüft niemand offline. */
export const isValidBic = (bicRaw: string | null | undefined): boolean => BIC_SHAPE.test(normalizeBic(bicRaw));

/**
 * Länderkennung der BIC (Zeichen 5–6) gegen die der IBAN. Nur ein HINWEIS, kein Blocker:
 * Fintechs (Revolut, Wise) führen IBANs teils unter einer BIC eines anderen Landes.
 */
export const bicCountryMatchesIban = (ibanRaw: string | null | undefined, bicRaw: string | null | undefined): boolean => {
    const iban = normalizeIban(ibanRaw);
    const bic = normalizeBic(bicRaw);

    if (iban.length < 2 || bic.length < 6) {
        return true;
    }

    return iban.slice(0, 2) === bic.slice(4, 6);
};

export type SepaBankDataIssue = "iban" | "bic";

/** Liefert den ersten harten Fehler einer Lastschrift-Bankverbindung oder null. */
export const getSepaBankDataIssue = (iban: string | null | undefined, bic: string | null | undefined): SepaBankDataIssue | null => {
    if (!isValidIban(iban)) {
        return "iban";
    }
    if (!isValidBic(bic)) {
        return "bic";
    }

    return null;
};
